import { useState, useEffect, PropsWithChildren } from "react";
import { Outlet } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import "overlayscrollbars/overlayscrollbars.css";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { defaultOverlayScrollbarsOptions } from "client-util/util";

import { PuzzleType } from "sockapi/puzzle-data";
import { ScopeType } from "sockapi/scope-types";
import { SockAPIScopeState } from "sockapi/scope-state";
import { TeamPuzzleSockAPIScopeState } from "sockapi/scope-specs/team-spec";
import { PuzzleContentType } from "sockapi/scope-specs/puzzle-content-spec";
import {
  useNavStore,
  useParsedSlug,
  useInPuzzlePage,
  useRound,
} from "stores/NavStore";
import { useTeamId, useSockClient } from "stores/ServerInteractionStore";
import { useUpdateSeparatelyPersistedState } from "stores/UserPreferencesStore";
import {
  CutsceneState,
  useCutsceneState,
  useUpdateBrowserWidePersistentState,
  useViewTimes,
} from "stores/BrowserWidePersistentState";
import {
  useTeamScopeState,
  useIsPuzzleSolved,
  useTeamAnswerableScopeState,
  useErrataContentForPuzzle,
  usePuzzleDisplayName,
  useAccessiblePuzzles,
} from "stores/ScopeStateStore";
import { LargeSpinner } from "components/Loading";
import {
  PuzzleContentRenderContext,
  usePuzzleContent,
} from "components/PuzzleContent/usePuzzleContent";
import { IslandFinder } from "components/PuzzleContent/IslandFinder";
import PuzzlePageToolbar from "components/PuzzlePage/PuzzlePageToolbar";
import SubmitAnswerForm from "components/PuzzlePage/SubmitAnswerForm";
import { addFlushSockAPIMessagesEffect } from "client-util/util";
import { toastr } from "Globals";
import "./PuzzlePage.css";

export const usePuzzleBodyDependencies = (
  puzData: TeamPuzzleSockAPIScopeState
): {
  teamId: string;
  /** Null only if the puzzle is not an answerable. */
  teamAnswerableScopeState: SockAPIScopeState<ScopeType.TEAM_ANSWERABLE> | null;
  errataContent: {
    erratumId: string;
    content: SockAPIScopeState<ScopeType.ERRATUM_CONTENT>;
  }[];
  renderCtx: PuzzleContentRenderContext;
  isPuzzleUnlocked: boolean;
  isPuzzleSolved: boolean;
  cutsceneState: CutsceneState;
} | null => {
  const { puzName, hasAnswer = false } = puzData;
  const teamId = useTeamId();
  const teamScopeState = useTeamScopeState();
  const errataContent = useErrataContentForPuzzle(puzName, true);
  const { renderCtx } = usePuzzleContent(puzName, PuzzleContentType.PUZZLE);
  const isPuzzleUnlocked =
    teamScopeState !== null
      ? teamScopeState.puzzles[puzName] !== undefined
      : null;
  // Only subscribe to answerable scope state if the puzzle is an answerable
  // and the team has it unlocked (otherwise the scope doesn't exist).
  const teamAnswerableScopeState = useTeamAnswerableScopeState(
    teamId,
    isPuzzleUnlocked && hasAnswer ? puzName : null
  );
  const isPuzzleSolved = useIsPuzzleSolved(puzName);
  const cutsceneState = useCutsceneState(puzName);

  if (teamId === null) return null;
  if (errataContent === null) return null;
  if (renderCtx === null) return null;
  if (isPuzzleUnlocked === null) return null;
  if (isPuzzleSolved === null) return null;

  return {
    teamId,
    errataContent,
    renderCtx,
    teamAnswerableScopeState,
    isPuzzleUnlocked,
    isPuzzleSolved,
    cutsceneState,
  };
};

function PuzzlePage() {
  const round = useRound();
  // send view puzzle req when selected puzzle changes
  const sockClient = useSockClient();
  const parsedSlug = useParsedSlug();
  const {
    slug = null,
    puzName = null,
    puzData: puzMetadata = null,
    desertIslandPieceId,
  } = parsedSlug ?? {};
  const updateSeparatelyPersistedState = useUpdateSeparatelyPersistedState();
  const updateBrowserWidePersistentState =
    useUpdateBrowserWidePersistentState();
  const viewTimes = useViewTimes();
  const { inPuzzlePage } = useInPuzzlePage();
  const [inPuzzle] = useState(useInPuzzlePage().inPuzzle);
  const setRedirectRoute = useNavStore((state) => state.setRedirectRoute);
  const viewTime = puzName !== null ? (viewTimes[puzName] ?? null) : null;
  const isSolved = useIsPuzzleSolved(puzName);
  const displayName = usePuzzleDisplayName(puzName);
  const teamScopeState = useTeamScopeState();
  const puzzles = useAccessiblePuzzles();

  useEffect(() => {
    if (puzName === null) return;
    if (viewTime !== null) return;
    updateBrowserWidePersistentState({
      viewTimes: {
        [puzName]: Date.now(),
      },
    });
  }, [sockClient, puzName, viewTime, updateBrowserWidePersistentState]);

  useEffect(() => {
    if (slug === null) return;
    // If the slug is invalid, don't save it.
    if (puzName === null) return;
    updateSeparatelyPersistedState({
      lastSeenPuzSlug: slug,
      lastSeenNonNullPuzSlug: slug,
    });
  }, [slug, puzName, updateSeparatelyPersistedState]);

  const needRedirectForInvalidPuzzle = (() => {
    // Handle effects going out of sync with React Router.
    if (!inPuzzlePage) return null;
    if (puzzles === null) return null;
    return (
      parsedSlug.puzName === null &&
      parsedSlug.desertIslandPieceId === undefined
    );
  })();
  useEffect(() => {
    if (!(needRedirectForInvalidPuzzle ?? false)) return;
    return addFlushSockAPIMessagesEffect(sockClient, () => {
      toastr.error("Invalid puzzle name.");
      // If the slug is invalid, redirect to the puzzles list.
      setRedirectRoute({ route: "puzzles", replace: true });
    });
  }, [needRedirectForInvalidPuzzle, setRedirectRoute, sockClient]);

  const hasAnswer =
    puzMetadata !== null &&
    (("hasAnswer" in puzMetadata && puzMetadata.hasAnswer === true) ||
      ("answer" in puzMetadata && puzMetadata.answer !== null));
  const puzData =
    puzMetadata !== null
      ? {
          hasAnswer: hasAnswer || undefined,
          unlockTime: 0,
          ...puzMetadata,
        }
      : null;
  const isUnlocked =
    puzName !== null && teamScopeState?.puzzles[puzName] !== undefined;
  const showSubmitAnswerForm = inPuzzle && hasAnswer && isUnlocked && !isSolved;

  // Let toastr know if we have a puzzle page with submit answer enabled.
  useEffect(() => {
    if (showSubmitAnswerForm)
      document.body.classList.add("has-submit-answer-form");
    else document.body.classList.remove("has-submit-answer-form");
    return () => {
      document.body.classList.remove("has-submit-answer-form");
    };
  }, [showSubmitAnswerForm]);

  if (
    needRedirectForInvalidPuzzle === null ||
    (desertIslandPieceId === undefined &&
      (puzData === null ||
        (puzData.type !== PuzzleType.STORY && round === null) ||
        puzName === null ||
        slug === null))
  )
    return <LargeSpinner inline={true} />;

  return (
    <PuzzlePageWrapper showSubmitAnswerForm={showSubmitAnswerForm}>
      <title>{displayName}</title>
      {puzData !== null ? (
        <>
          <PuzzlePageToolbar puzData={puzData} />
          <Outlet context={puzData} />
        </>
      ) : desertIslandPieceId !== undefined ? (
        <IslandFinder />
      ) : null}
    </PuzzlePageWrapper>
  );
}

export function PuzzlePageWrapper({
  children,
  showSubmitAnswerForm = false,
}: PropsWithChildren<{ showSubmitAnswerForm?: boolean }>) {
  return (
    <div className="puzzle-page-root">
      <OverlayScrollbarsComponent
        options={defaultOverlayScrollbarsOptions}
        defer
        style={{ flex: 1 }}
      >
        <div className="puzzle-page-content">{children}</div>
      </OverlayScrollbarsComponent>
      <AnimatePresence>
        {showSubmitAnswerForm ? <SubmitAnswerForm /> : null}
      </AnimatePresence>
    </div>
  );
}

export default PuzzlePage;
